import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  HiBellAlert,
  HiBookOpen,
  HiChatBubbleLeftRight,
  HiCog6Tooth,
  HiHome,
  HiInbox,
  HiMagnifyingGlass,
  HiOutlineBellAlert,
  HiOutlineBookOpen,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCog6Tooth,
  HiOutlineHome,
  HiOutlineInbox,
  HiOutlineSquare3Stack3D,
  HiSquare3Stack3D,
} from 'react-icons/hi2'

import { useAuthInfo } from '@sweetauth/core'

import { CUI_DISABLED_FEATURE_TOOLTIP } from '@/components/common/Sidebar/cui'

import { createLoginUrlWithRedirect } from '@/helpers/login'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useCanAccessAwardRecipientSearch } from '@/hooks/billing/useCanAccessAwardRecipientSearch'
import { useIsTrialPeriod } from '@/hooks/billing/useIsTrialPeriod'
import { usePathname } from '@/hooks/common/usePathname'
import { useCanAccessMonitors } from '@/hooks/monitors/useCanAccessMonitors'
import { useHandleOrgChange } from '@/hooks/navigation/useHangleOrgChange'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useIsOnboarding } from '@/hooks/onboarding/useIsOnboarding'
import { useActiveOrg } from '@/hooks/organization/useActiveOrg'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useDefaultOrganization } from '@/hooks/organization/useDefaultOrganization'
import { useOrganizations } from '@/hooks/organization/useOrganizations'
import { useUpdateDefaultOrganization } from '@/hooks/organization/useUpdateDefaultOrganization'
import { usePermissionCheck } from '@/hooks/permissions/usePermissionCheck'
import { useCanAccessVehicles } from '@/hooks/vehicles'

import { useOrganizationStore } from '@/stores/OrganizationStore'
import { useSidebarStore, useSidebarVariant } from '@/stores/SidebarStore'

import type { OrganizationTier } from '@/types/BillingOrganization'
import type { UUID } from '@/types/Id'
import type { NavigationOption } from '@/types/navigation/Sidebar'
import type { Organization } from '@/types/Organization'

export const useSidebar = () => {
  const { isTrialPeriod } = useIsTrialPeriod()
  const { activeOrgId } = useActiveOrgId()
  const { user } = useAuthInfo()
  const { safePush } = useSafePush()
  const { isOnboarding } = useIsOnboarding()
  const { data: permissionData, isLoading: isLoadingPermission } =
    usePermissionCheck({
      resourceType: 'organization',
      resourceId: activeOrgId as UUID,
      permissions: ['read'],
      enabled: !!activeOrgId,
    })

  const canAccessSearch =
    isLoadingPermission || permissionData?.get('read') === true

  const {
    data: billingOrganization,
    isLoading: billingOrgIsLoading,
    retryCount,
  } = useBillingOrganization()

  // if the user is onboarding, we don't want to load the default organization (to prevent 404 errors)
  const { data: defaultOrganization, isLoading: defaultOrgIsLoading } =
    useDefaultOrganization({ enabled: !isOnboarding })

  const {
    data: organizations,
    refetch: refetchOrganizations,
    isLoading: organizationsAreLoading,
  } = useOrganizations()

  const { mutate: updateDefaultOrganization } = useUpdateDefaultOrganization()
  const { toggleSidebar } = useSidebarStore()
  const { handleOrgChange } = useHandleOrgChange()
  const { setActiveOrgId } = useOrganizationStore()

  const [addOrgModalIsOpen, setAddOrgModalIsOpen] = useState<boolean>(false)

  const pathname = usePathname()

  const initialFocusRef = useRef<HTMLDivElement>(null)

  const canEditOrganizations = useMemo(() => {
    return (
      billingOrganization !== undefined &&
      billingOrganization !== null &&
      billingOrganization.tier !== 'free' &&
      billingOrganization.tier !== 'basic'
    )
  }, [billingOrganization])

  const dataIsLoading = useMemo(() => {
    if (user === null) {
      return false
    }
    return billingOrgIsLoading || defaultOrgIsLoading || organizationsAreLoading
  }, [billingOrgIsLoading, defaultOrgIsLoading, organizationsAreLoading, user])

  const myTasksIsActive = useMemo(
    // for some reason, pathname is occassionally null when the component first mounts
    () => (pathname !== null ? pathname.startsWith('/tasks') : false),
    [pathname],
  )
  const myDocumentsIsActive = useMemo(
    // for some reason, pathname is occassionally null when the component first mounts
    () => (pathname !== null ? pathname.startsWith('/documents') : false),
    [pathname],
  )

  const sortedOrganizations = useMemo(() => {
    return organizations?.sort((a: Organization, b: Organization) =>
      a.name.localeCompare(b.name),
    )
  }, [organizations])

  const isEligibleForAccess = (itemMinTier: OrganizationTier): boolean => {
    const tierOrder: Array<OrganizationTier> = [
      'free',
      'starter',
      'basic',
      'premium',
      'enterprise',
    ]
    const currentTierIndex = tierOrder.indexOf(
      billingOrganization?.tier || 'free',
    )
    const requiredTierIndex = tierOrder.indexOf(itemMinTier)
    return currentTierIndex >= requiredTierIndex
  }

  const userCanAccess = (item: NavigationOption) => {
    if (user === undefined || user === null) {
      return false
    }

    const hasTierAccess =
      item.accessOverride !== undefined
        ? item.accessOverride
        : isEligibleForAccess(item.minOrganizationTier) ||
          (item.isTrialEligible && isTrialPeriod)

    if (!hasTierAccess) {
      return false
    }

    if (item.isOrganizationFeature && activeOrgId === '') {
      return false
    }

    if (item.requiresOrganizationRead) {
      return canAccessSearch
    }

    return true
  }

  const isActive = (option: NavigationOption) => {
    // for some reason, pathname is undefined when the page is not mounted,
    // even though typescript says it's a string
    if (pathname === undefined || pathname === null) {
      return false
    }
    return option.match(pathname)
  }

  const handleAddOrganization = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation()
    setAddOrgModalIsOpen(true)
  }

  const handleOrganizationChange = (orgId: string, toggle: boolean = true) => {
    // WARNING: DO NOT USE setActiveOrgId IN HERE;
    // FOR SOME REASON, IT CAUSES THE NEW PAGE TO DOUBLE RENDER;
    // TODO: figure this out
    handleOrgChange(orgId)
    if (
      orgId &&
      organizations &&
      defaultOrganization &&
      defaultOrganization.id !== orgId
    ) {
      const org = organizations.find((org) => org.id === orgId)
      if (org) {
        updateDefaultOrganization([org])
      }
    }
    if (toggle) {
      toggleSidebar()
    }
  }

  const handleSignupClick = () => {
    safePush(createLoginUrlWithRedirect(window.location.href))
  }

  const handleUpgradeClick = () => {
    safePush('/upgrade')
    toggleSidebar()
  }

  useEffect(() => {
    if (
      // do not set the active org id if the user is onboarding,
      // this causes a maximum recursion depth exceeded error;
      // TODO: figure out why
      !isOnboarding &&
      billingOrganization &&
      defaultOrganization &&
      organizations !== undefined &&
      organizations.length > 0 &&
      (activeOrgId === '' || activeOrgId === undefined)
    ) {
      const org = organizations.find((org) => org.id === defaultOrganization.id)
      if (org) {
        setActiveOrgId(org.id)
      } else {
        setActiveOrgId(organizations[0].id)
      }
    }
  }, [activeOrgId, defaultOrganization, organizations, organizationsAreLoading])

  return {
    addOrgModalIsOpen,
    canEditOrganizations,
    initialFocusRef,
    isLoading: dataIsLoading,
    myTasksIsActive,
    myDocumentsIsActive,
    organizations: sortedOrganizations,
    handleAddOrganization,
    handleOrganizationChange,
    handleSignupClick,
    handleUpgradeClick,
    isActive,
    setAddOrgModalIsOpen,
    userCanAccess,
  }
}

const SAVED_SEARCHES_OPTION = {
  id: 'schedules' as const,
  name: 'Saved Searches',
  href: '/schedules',
  icon: HiOutlineBellAlert,
  activeIcon: HiBellAlert,
  current: false,
  isTrialEligible: true,
  isOrganizationFeature: true,
  requiresOrganizationRead: true,
  minOrganizationTier: 'basic' as const,
  match: (pathname: string) => pathname.endsWith('/schedules'),
} as const

const SETTINGS_OPTION = {
  id: 'settings' as const,
  name: 'Settings',
  href: '/profile',
  icon: HiOutlineCog6Tooth,
  activeIcon: HiCog6Tooth,
  isTrialEligible: true,
  isOrganizationFeature: true,
  minOrganizationTier: 'basic' as const,
  current: false,
  match: (pathname: string) =>
    pathname.includes('/profile') &&
    pathname !== '/' &&
    !pathname.includes('/library'),
} as const

export const useNavigationOptions = () => {
  const sidebarVariant = useSidebarVariant()
  const { activeOrg } = useActiveOrg()
  const { canAccessAwardRecipientSearch } = useCanAccessAwardRecipientSearch()
  const { canAccessVehicles } = useCanAccessVehicles()
  const { canAccessMonitors } = useCanAccessMonitors()

  const isDisabledForCui = activeOrg?.cui_compliance_enabled === true

  const navigationOptions: NavigationOption[] = useMemo(() => {
    let defaultOpportunityTypeLink = '/federal-contracts/search'
    if (activeOrg) {
      switch (activeOrg.default_opportunity_type) {
        case 'dibbs_contract':
          defaultOpportunityTypeLink = '/dibbs-contracts/search'
          break
        case 'federal_contract':
          defaultOpportunityTypeLink = '/federal-contracts/search'
          break
        case 'federal_grant':
          defaultOpportunityTypeLink = '/federal-grants/search'
          break
        case 'sbir':
          defaultOpportunityTypeLink = '/sbirs/search'
          break
        case 'sled_contract':
          defaultOpportunityTypeLink = '/sled-contracts/search'
          break
        default:
          const exhaustiveCheck: never = activeOrg?.default_opportunity_type
          throw new Error(
            `Unknown opportunity type: ${activeOrg?.default_opportunity_type}`,
          )
      }
    }
    return [
      {
        id: 'home' as const,
        name: 'Home',
        href: '/',
        icon: HiOutlineHome,
        activeIcon: HiHome,
        current: false,
        isTrialEligible: true,
        isOrganizationFeature: false,
        minOrganizationTier: 'basic' as const,
        childrenOpen: false,
        match: (pathname: string) => pathname === '/' || pathname === '',
      },
      // Inbox sits directly under Home on the rail, above Search — the primary
      // entry point into Monitors. Monitors itself is not a top-level rail icon;
      // it lives in the Search flyout, nested under Saved searches.
      ...(canAccessMonitors
        ? [
            {
              id: 'inbox' as const,
              name: 'Inbox',
              href: '/monitors/inbox',
              icon: HiOutlineInbox,
              activeIcon: HiInbox,
              isTrialEligible: true,
              isOrganizationFeature: true,
              requiresOrganizationRead: true,
              minOrganizationTier: 'basic' as const,
              current: false,
              accessOverride: canAccessMonitors && !isDisabledForCui,
              disabledTooltip: isDisabledForCui
                ? CUI_DISABLED_FEATURE_TOOLTIP
                : undefined,
              match: (pathname: string) => pathname.includes('/monitors/inbox'),
            },
          ]
        : []),
      {
        id: 'search' as const,
        name: 'Search',
        href: defaultOpportunityTypeLink,
        icon: HiMagnifyingGlass,
        activeIcon: HiMagnifyingGlass,
        current: false,
        isTrialEligible: true,
        isOrganizationFeature: false,
        requiresOrganizationRead: true,
        minOrganizationTier: 'basic' as const,
        childrenOpen: false,
        match: (pathname: string) =>
          pathname.startsWith('/opportunity') ||
          pathname.startsWith('/federal-contracts') ||
          pathname.startsWith('/federal-grants') ||
          pathname.startsWith('/dibbs-contracts') ||
          pathname.startsWith('/sbirs') ||
          pathname.startsWith('/sled-contracts') ||
          pathname.startsWith('/awards') ||
          pathname.startsWith('/recipients') ||
          pathname.startsWith('/agencies') ||
          pathname.startsWith('/forecasts') ||
          pathname.startsWith('/vehicles') ||
          pathname.startsWith('/naics') ||
          pathname.startsWith('/psc') ||
          pathname.endsWith('/schedules') ||
          pathname.includes('/bookmarks') ||
          // Monitors now lives inside this Search flyout (nested under Saved
          // searches), so the Search icon owns the monitors dashboard. The
          // Inbox subpath is excluded — the standalone Inbox rail item owns it.
          (pathname.includes('/monitors') &&
            !pathname.includes('/monitors/inbox')),
        // Children are used by MobileSidebar; desktop uses custom SearchFlyoutPanel
        children: [
          {
            id: 'federal-contracts' as const,
            name: 'Federal Contracts',
            href: '/federal-contracts/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'basic' as const,
            match: (pathname: string) =>
              pathname.startsWith('/federal-contracts') && pathname !== '/',
          },
          {
            id: 'federal-grants' as const,
            name: 'Federal Grants',
            href: '/federal-grants/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'basic' as const,
            match: (pathname: string) =>
              pathname.startsWith('/federal-grants') && pathname !== '/',
          },
          {
            id: 'dibbs-contracts' as const,
            name: 'DIBBS Contracts',
            href: '/dibbs-contracts/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'basic' as const,
            match: (pathname: string) =>
              pathname.startsWith('/dibbs-contracts') && pathname !== '/',
          },
          {
            id: 'sbirs' as const,
            name: 'SBIR/STTRs',
            href: '/sbirs/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'basic' as const,
            match: (pathname: string) =>
              pathname.startsWith('/sbirs') && pathname !== '/',
          },
          {
            id: 'sled-contracts' as const,
            name: 'State & Local',
            href: '/sled-contracts/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'basic' as const,
            match: (pathname: string) =>
              pathname.startsWith('/sled-contracts') && pathname !== '/',
          },
          {
            id: 'award-search' as const,
            name: 'Federal Award AI',
            href: '/awards/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'premium' as const,
            accessOverride: canAccessAwardRecipientSearch,
            match: (pathname: string) => pathname.startsWith('/awards/search'),
          },
          {
            id: 'recipient-search' as const,
            name: 'Recipients',
            href: '/recipients/search',
            icon: null,
            activeIcon: null,
            current: false,
            isTrialEligible: true,
            isOrganizationFeature: false,
            minOrganizationTier: 'premium' as const,
            accessOverride: canAccessAwardRecipientSearch,
            match: (pathname: string) =>
              pathname.startsWith('/recipients/search'),
          },
          // even though the entitlement is called "vehicles",
          // it's actually used for all data types here
          ...(!isDisabledForCui && canAccessVehicles
            ? [
                {
                  id: 'forecast-search' as const,
                  name: 'Forecasts',
                  href: '/forecasts/federal/search',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: true,
                  isOrganizationFeature: false,
                  minOrganizationTier: 'premium' as const,
                  accessOverride: canAccessVehicles,
                  match: (pathname: string) =>
                    pathname.startsWith('/forecasts/federal/search'),
                },
                {
                  id: 'budget-search' as const,
                  name: 'Budgets',
                  href: '/budget/federal/search',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: false,
                  isOrganizationFeature: false,
                  minOrganizationTier: 'free' as const,
                  accessOverride: canAccessVehicles,
                  match: (pathname: string) =>
                    pathname.startsWith('/budget/federal/search') ||
                    pathname.startsWith('/budget/federal/accounts'),
                },
                {
                  id: 'protest-search' as const,
                  name: 'Protests',
                  href: '/protests/federal/search',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: false,
                  isOrganizationFeature: false,
                  minOrganizationTier: 'free' as const,
                  accessOverride: canAccessVehicles,
                  match: (pathname: string) =>
                    pathname.startsWith('/protests/federal/search') ||
                    pathname.startsWith('/protests/federal/'),
                },
                {
                  id: 'vehicle-search' as const,
                  name: 'Vehicles',
                  href: '/vehicles/federal/search',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: false,
                  isOrganizationFeature: false,
                  minOrganizationTier: 'free' as const,
                  accessOverride: canAccessVehicles,
                  match: (pathname: string) =>
                    pathname.startsWith('/vehicles/federal/search'),
                },
                {
                  id: 'naics-search' as const,
                  name: 'NAICS',
                  href: '/naics/federal/search',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: false,
                  isOrganizationFeature: false,
                  minOrganizationTier: 'free' as const,
                  accessOverride: canAccessVehicles,
                  match: (pathname: string) =>
                    pathname.startsWith('/naics/federal/search'),
                },
                {
                  id: 'psc-search' as const,
                  name: 'PSC',
                  href: '/psc/federal/search',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: false,
                  isOrganizationFeature: false,
                  minOrganizationTier: 'free' as const,
                  accessOverride: canAccessVehicles,
                  match: (pathname: string) =>
                    pathname.startsWith('/psc/federal/search'),
                },
              ]
            : []),
          // Monitors is nested under Search (desktop renders it via the custom
          // SearchFlyout; mobile/legacy render it from these children). The
          // array spread itself carries the full gate — the legacy children
          // renderer draws plain links without an access check, so CUI orgs
          // must be excluded here (mirrors the vehicles children above), not
          // just via accessOverride.
          ...(canAccessMonitors && !isDisabledForCui
            ? [
                {
                  id: 'monitors' as const,
                  name: 'Monitors',
                  href: '/monitors',
                  icon: null,
                  activeIcon: null,
                  current: false,
                  isTrialEligible: true,
                  isOrganizationFeature: true,
                  requiresOrganizationRead: true,
                  minOrganizationTier: 'basic' as const,
                  accessOverride: canAccessMonitors && !isDisabledForCui,
                  match: (pathname: string) =>
                    pathname.includes('/monitors') &&
                    !pathname.includes('/monitors/inbox') &&
                    pathname !== '/',
                },
              ]
            : []),
          ...(sidebarVariant === 'standard' ? [SAVED_SEARCHES_OPTION] : []),
        ],
      },
      ...(sidebarVariant === 'legacy' ? [SAVED_SEARCHES_OPTION] : []),
      {
        id: 'ask-ai' as const,
        name: 'Ask AI',
        href: '/c/new',
        icon: HiOutlineChatBubbleLeftRight,
        activeIcon: HiChatBubbleLeftRight,
        isTrialEligible: true,
        isOrganizationFeature: true,
        minOrganizationTier: 'basic' as const,
        current: false,
        match: (pathname: string) =>
          pathname.includes('/c/') &&
          !pathname.includes('/pursuits/') &&
          pathname !== '/',
      },
      // {
      //   name: 'Pipelines',
      //   href: '/pipelines',
      //   icon: HiOutlineQueueList,
      //   isTrialEligible: true,
      //   isOrganizationFeature: true,
      //   minOrganizationTier: 'basic',
      //   current: false,
      //   match: (pathname: string) => pathname.includes('/pipelines'),
      // },
      {
        id: 'pursuits' as const,
        name: 'Pursuits',
        href: '/pursuits',
        icon: HiOutlineSquare3Stack3D,
        activeIcon: HiSquare3Stack3D,
        // icon: HiOutlineCurrencyDollar,
        // activeIcon: HiCurrencyDollar,
        isTrialEligible: true,
        isOrganizationFeature: true,
        minOrganizationTier: 'basic' as const,
        current: false,
        viewAllChildrenHref: '/pursuits',
        childrenTitle: 'Recently Viewed',
        match: (pathname: string) =>
          pathname.includes('/pursuits') && pathname !== '/',
      },
      {
        id: 'library' as const,
        name: 'Library',
        href: '/library/files',
        icon: HiOutlineBookOpen,
        activeIcon: HiBookOpen,
        isTrialEligible: false,
        isOrganizationFeature: true,
        minOrganizationTier: 'premium' as const,
        current: false,
        viewAllChildrenHref: '/library/files',
        match: (pathname: string) =>
          pathname.includes('/library') && pathname !== '/',
      },
      ...(sidebarVariant === 'legacy' ? [SETTINGS_OPTION] : []),
    ]
  }, [
    activeOrg?.cui_compliance_enabled,
    activeOrg?.default_opportunity_type,
    canAccessAwardRecipientSearch,
    canAccessMonitors,
    canAccessVehicles,
    isDisabledForCui,
    sidebarVariant,
  ])

  return {
    navigationOptions,
    settingsOption: SETTINGS_OPTION,
  }
}
